import { AlertCircle } from 'lucide-react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useJudgeSummary } from '../../hooks/useJudgeSummary';
import { useJudgeVerdicts } from '../../hooks/useJudgeVerdicts';
import { getApiErrorDetails } from '../../utils/api-error';

import { stockholmToday } from './date-range';
import { BAD_OUTCOMES } from './kvalitet-labels';
import {
  activePresetKey,
  isRootCause,
  parseKvalitetState,
  toPeriodParams,
  toVerdictParams,
  withKvalitetPatch,
  type KvalitetPatch,
} from './kvalitet-url-state';
import { KvalitetFilterBar } from './KvalitetFilterBar';
import { KvalitetOutcomeCards } from './KvalitetOutcomeCards';
import { KvalitetPatternsTable } from './KvalitetPatternsTable';
import { KvalitetRootCauseCards } from './KvalitetRootCauseCards';
import { KvalitetSessionTable } from './KvalitetSessionTable';
import { KvalitetTrendBars } from './KvalitetTrendBars';

export const KvalitetPanel = () => {
  const today = stockholmToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseKvalitetState(searchParams, today), [searchParams, today]);
  const update = (patch: KvalitetPatch) =>
    setSearchParams(withKvalitetPatch(searchParams, patch), { replace: true });

  const periodParams = useMemo(() => toPeriodParams(state), [state]);
  const verdictParams = useMemo(() => toVerdictParams(state), [state]);
  const summary = useJudgeSummary(periodParams);
  const verdicts = useJudgeVerdicts(verdictParams);
  const data = summary.data ?? null;

  return (
    <div className="space-y-6">
      <KvalitetFilterBar
        state={state}
        today={today}
        activePreset={activePresetKey(state, today)}
        promptVersions={data?.promptVersions ?? []}
        promptVersion={state.promptVersion ?? data?.promptVersion ?? null}
        loading={summary.isFetching}
        onPeriod={(period) => update({ period })}
        onPromptVersion={(promptVersion) => update({ promptVersion })}
        onClearJudged={() => update({ clearJudged: true })}
      />

      {summary.error && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>Kunde inte ladda sammanställningen: {getApiErrorDetails(summary.error)}</span>
        </div>
      )}

      {data && (
        <>
          <section className="space-y-4">
            <KvalitetOutcomeCards
              byOutcome={data.byOutcome}
              totals={data.totals}
              selected={state.outcomes}
              onSelect={(outcome) =>
                update({
                  outcomes:
                    state.outcomes.length === 1 && state.outcomes[0] === outcome ? [] : [outcome],
                })
              }
            />
            <KvalitetRootCauseCards
              byRootCause={data.byRootCause}
              selected={state.rootCause}
              onSelect={(rootCause) =>
                update({ rootCause: state.rootCause === rootCause ? null : rootCause })
              }
            />
            <KvalitetTrendBars daily={data.daily} basis={data.period.basis} />
          </section>

          <KvalitetPatternsTable
            patterns={data.patterns}
            onShowWeek={(pattern) =>
              update({
                period: { from: pattern.weekFrom, to: pattern.weekTo },
                outcomes: BAD_OUTCOMES,
                rootCause: isRootCause(pattern.rootCause) ? pattern.rootCause : null,
              })
            }
          />
        </>
      )}

      <KvalitetSessionTable
        list={verdicts}
        outcomes={state.outcomes}
        rootCause={state.rootCause}
        onClearOutcomes={() => update({ outcomes: [] })}
        onClearRootCause={() => update({ rootCause: null })}
      />
    </div>
  );
};
